import { Box, useMantineTheme } from '@mantine/core';
import { keyframes } from '../utils/keyframes';;
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export type SpinnerDotsVariant = 'bounce' | 'fade' | 'scale' | 'wave';

export interface SpinnerDotsProps {
  size?: number;
  color?: string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  variant?: SpinnerDotsVariant;
  count?: number;
  speed?: number;
}

const SpinnerDots = forwardRef<HTMLDivElement, SpinnerDotsProps>(
  (
    {
      size = 10,
      color,
      gradientVariant = 'primary',
      variant = 'bounce',
      count = 3,
      speed = 1400,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const reducedMotion = useReducedMotion();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const dotColor = color || gradient?.from || theme.primaryColor;

    const bounceKeyframes = keyframes({
      '0%, 80%, 100%': { transform: 'scale(0)' },
      '40%': { transform: 'scale(1)' },
    });

    const fadeKeyframes = keyframes({
      '0%, 80%, 100%': { opacity: 0 },
      '40%': { opacity: 1 },
    });

    const scaleKeyframes = keyframes({
      '0%, 100%': { transform: 'scale(1)' },
      '50%': { transform: 'scale(1.5)' },
    });

    const waveKeyframes = keyframes({
      '0%, 40%, 100%': { transform: 'translateY(0)' },
      '20%': { transform: `translateY(-${size}px)` },
    });

    const getAnimation = (index: number) => {
      if (reducedMotion) return 'none';

      const delay = (index * speed) / count / 3;

      switch (variant) {
        case 'bounce':
          return `${bounceKeyframes} ${speed}ms ease-in-out ${delay}ms infinite`;
        case 'fade':
          return `${fadeKeyframes} ${speed}ms ease-in-out ${delay}ms infinite`;
        case 'scale':
          return `${scaleKeyframes} ${speed}ms ease-in-out ${delay}ms infinite`;
        case 'wave':
          return `${waveKeyframes} ${speed}ms ease-in-out ${delay}ms infinite`;
        default:
          return `${bounceKeyframes} ${speed}ms ease-in-out ${delay}ms infinite`;
      }
    };

    return (
      <Box
        ref={ref}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: size / 2,
        }}
      >
        {Array.from({ length: count }, (_, i) => (
          <Box
            key={i}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: dotColor,
              animation: getAnimation(i),
            }}
          />
        ))}
      </Box>
    );
  }
);

SpinnerDots.displayName = 'SpinnerDots';

export default SpinnerDots;
